import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LinkAdaptativo.module.css';

/**
 * LinkAdaptativo: Componente que extiende Link de react-router-dom
 *
 * Comportamiento:
 * - DESKTOP: abre links en nueva ventana (target="_blank", rel="noopener noreferrer")
 * - MOBILE: abre links en la misma ventana (comportamiento estándar)
 *
 * Ventajas:
 * - UX mejorada en mobile (evita lagunas de ventanas)
 * - Sin subrayado ni color azul (hereda estilos del padre)
 * - API idéntica a Link, solo agregar `external={true}`
 *
 * Uso:
 *   <LinkAdaptativo to="/clubes?pais=1" external={true}>
 *     Ver clubes
 *   </LinkAdaptativo>
 *
 * Props:
 *   - external: boolean (default: false) - si true, aplica comportamiento adaptativo
 *   - to: string - ruta de destino (requerido)
 *   - children: node - contenido del link
 *   - className: string (opcional) - clases CSS
 *   - ...props: otros props se pasan a Link
 */
const LinkAdaptativo = ({ external = false, to, children, className = '', ...props }) => {
  // Detectar si está en mobile
  const isMobile = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    // Usar media query para detectar viewport pequeño
    return window.matchMedia('(max-width: 768px)').matches;
  };

  // Si no es link externo, usar Link estándar
  if (!external) {
    return (
      <Link to={to} className={`${styles.linkAdaptativo} ${className}`.trim()} {...props}>
        {children}
      </Link>
    );
  }

  // Si es link externo, adaptar según dispositivo
  const isOnMobile = isMobile();
  const target = isOnMobile ? undefined : '_blank';
  const rel = isOnMobile ? undefined : 'noopener noreferrer';

  return (
    <Link
      to={to}
      target={target}
      rel={rel}
      className={`${styles.linkAdaptativo} ${className}`.trim()}
      {...props}
    >
      {children}
    </Link>
  );
};

export default LinkAdaptativo;
